import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CommunityLayout } from '../components/CommunityLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import {
  Users,
  Search,
  Plus,
  MessageSquare,
  Heart,
  Eye,
  ChevronRight,
  BookOpen,
  HelpCircle,
  Megaphone,
  CheckCircle,
  UserCheck,
  Calendar,
  MapPin,
  Video,
} from 'lucide-react';
import { communityApi, CommunityPost } from '../services/communityApi';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow, format } from 'date-fns';
import { nl } from 'date-fns/locale';

const POST_TYPE_CONFIG = {
  article: { label: 'community.postType.article', icon: BookOpen, color: 'bg-blue-100 text-blue-700' },
  question: { label: 'community.postType.question', icon: HelpCircle, color: 'bg-yellow-100 text-yellow-700' },
  discussion: { label: 'community.postType.discussion', icon: MessageSquare, color: 'bg-green-100 text-green-700' },
  announcement: { label: 'community.postType.announcement', icon: Megaphone, color: 'bg-purple-100 text-purple-700' },
};

const PostCard: React.FC<{ post: CommunityPost }> = ({ post }) => {
  const { t } = useTranslation('pages');
  const typeConf = POST_TYPE_CONFIG[post.type] || POST_TYPE_CONFIG.discussion;
  const TypeIcon = typeConf.icon;
  const excerptText = post.excerpt || '';
  const isLong = excerptText.length > 150;
  const displayExcerpt = isLong ? excerptText.slice(0, 150) + '…' : excerptText;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#0B1F3A] hover:shadow-sm transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: post.category_color + '20', color: post.category_color }}
            >
              {post.category_professional_type ? t(`screening.types.${post.category_professional_type}`, { defaultValue: post.category_name }) : post.category_name}
            </span>
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${typeConf.color}`}>
              <TypeIcon className="w-3 h-3" />
              {t(typeConf.label)}
            </span>
          </div>
          <Link to={`/community/bericht/${post.id}`} className="group">
            <h3 className="font-semibold text-gray-900 group-hover:text-[#0B1F3A] transition-colors line-clamp-2">
              {post.title}
            </h3>
          </Link>
          {post.featured_image && (
            <Link to={`/community/bericht/${post.id}`} className="block mt-2 rounded-md overflow-hidden">
              <img src={post.featured_image} alt={post.title} className="w-full h-32 object-cover" />
            </Link>
          )}
          {displayExcerpt && (
            <p className="text-sm text-gray-500 mt-1">
              {displayExcerpt}
              {isLong && (
                <Link
                  to={`/community/bericht/${post.id}`}
                  className="ml-1 text-[#0B1F3A] hover:underline font-medium whitespace-nowrap"
                >
                  {t('community.readMore')}
                </Link>
              )}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
        <span>
          {post.first_name} {post.last_name} ·{' '}
          {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: nl })}
        </span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Heart className="w-3.5 h-3.5" /> {post.likes_count}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" /> {post.comments_count}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-3.5 h-3.5" /> {post.views_count}
          </span>
        </div>
      </div>
    </div>
  );
};

export const CommunityPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [showMyChannels, setShowMyChannels] = useState(false);
  const [showChannels, setShowChannels] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: channels = [], isLoading: channelsLoading } = useQuery({
    queryKey: ['community-channels'],
    queryFn: communityApi.getChannels,
  });

  const { data: posts = [], isLoading: postsLoading } = useQuery({
    queryKey: ['community-posts', activeSearch, showMyChannels],
    queryFn: () =>
      communityApi.getPosts({
        search: activeSearch || undefined,
        my_channels: showMyChannels || undefined,
        limit: 20,
      }),
  });

  const joinMutation = useMutation({
    mutationFn: communityApi.joinChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-channels'] });
      toast.success(t('community.joinedChannel'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const leaveMutation = useMutation({
    mutationFn: communityApi.leaveChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-channels'] });
      toast.success(t('community.leftChannel'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(searchTerm);
  };

  const { data: events = [] } = useQuery({
    queryKey: ['community-events'],
    queryFn: async () => {
      const res = await api.get('/community/events');
      return res.data.data || [];
    },
  });

  const attendMutation = useMutation({
    mutationFn: (eventId: string) => api.post(`/community/events/${eventId}/attend`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-events'] });
      toast.success('Aanmelding bijgewerkt');
    },
    onError: () => toast.error('Kon aanmelding niet bijwerken'),
  });

  const myChannels = channels.filter((c) => c.is_member);
  const totalMembers = channels.reduce((sum, c) => sum + c.member_count, 0);

  return (
    <CommunityLayout>
    <div className="py-8 ml-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-end">
          <Button onClick={() => navigate('/community/nieuw-bericht')}>
            <Plus className="w-4 h-4 mr-2" />
            {t('community.newPost')}
          </Button>
        </div>

        {/* Stats bar */}
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-500">
          <span className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-[#0B1F3A]" />
            <span className="font-medium text-gray-900">{totalMembers}</span> {t('community.totalParticipants')}
          </span>
          <span className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-[#0B1F3A]" />
            <span className="font-medium text-gray-900">{myChannels.length}</span> {t('community.channelsFollowed', { total: channels.length })}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: feed */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search + filter bar */}
          <Card>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('community.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-transparent"
                />
              </div>
              <Button type="submit" variant="outline" size="sm">
                {t('community.search')}
              </Button>
              {activeSearch && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setActiveSearch(''); setSearchTerm(''); }}
                >
                  {t('community.clear')}
                </Button>
              )}
            </form>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setShowMyChannels(false)}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  !showMyChannels
                    ? 'bg-[#0B1F3A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('community.allPosts')}
              </button>
              <button
                onClick={() => setShowMyChannels(true)}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  showMyChannels
                    ? 'bg-[#0B1F3A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('community.myChannels')}
              </button>
            </div>
          </Card>

          {/* Posts */}
          {postsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <Card>
              <div className="text-center py-10">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">
                  {activeSearch ? t('community.noPostsSearch') : t('community.noPosts')}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {showMyChannels && myChannels.length === 0
                    ? t('community.joinChannelsToSee')
                    : t('community.beFirstToPost')}
                </p>
                <Button
                  className="mt-4"
                  size="sm"
                  onClick={() => navigate('/community/nieuw-bericht')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('community.placePost')}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>

        {/* Right: channels sidebar */}
        <div className="space-y-4">
          {/* Kanalen toggle */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#0B1F3A]" />
                {t('community.channels')}
              </h2>
              <button
                onClick={() => setShowChannels(!showChannels)}
                className="text-sm text-[#0B1F3A] hover:underline"
              >
                {showChannels ? t('community.hide') : t('community.showAll')}
              </button>
            </div>

            {channelsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {(showChannels ? channels : channels.slice(0, 5)).map((channel) => (
                  <div
                    key={channel.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group"
                  >
                    <Link
                      to={`/community/kanaal/${channel.slug}`}
                      className="flex-1 min-w-0"
                    >
                      <div className="flex items-center gap-2">
                        {channel.is_member && (
                          <UserCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-gray-800 truncate group-hover:text-[#0B1F3A]">
                          {t(`screening.types.${channel.professional_type}`, { defaultValue: channel.name })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {channel.member_count} {t('community.members')}
                      </p>
                    </Link>
                    <button
                      onClick={() =>
                        channel.is_member
                          ? leaveMutation.mutate(channel.id)
                          : joinMutation.mutate(channel.id)
                      }
                      disabled={joinMutation.isPending || leaveMutation.isPending}
                      className={`ml-2 text-xs px-2 py-1 rounded transition-colors flex-shrink-0 ${
                        channel.is_member
                          ? 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600'
                          : 'bg-[#0B1F3A] text-white hover:bg-[#1A3459]'
                      }`}
                    >
                      {channel.is_member ? t('community.unsubscribe') : t('community.subscribe')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!showChannels && channels.length > 5 && (
              <button
                onClick={() => setShowChannels(true)}
                className="mt-3 w-full text-sm text-[#0B1F3A] hover:underline flex items-center justify-center gap-1"
              >
                {t('community.showAllChannels', { count: channels.length })} <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </Card>

          {/* Evenementen */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-[#0B1F3A]" />
              <h2 className="font-semibold text-gray-900">Aankomende evenementen</h2>
            </div>
            {events.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Geen evenementen gepland</p>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 4).map((event: any) => (
                  <div key={event.id} className="border border-gray-100 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-900 leading-tight">{event.title}</p>
                    <div className="mt-1.5 flex items-center gap-1 text-xs text-blue-700">
                      <Calendar className="w-3 h-3" />
                      {format(new Date(event.event_date), 'd MMM yyyy HH:mm', { locale: nl })}
                    </div>
                    {event.online ? (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <Video className="w-3 h-3" />
                        Online
                      </div>
                    ) : event.location ? (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </div>
                    ) : null}
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-gray-400">{event.attendee_count} aangemeld</span>
                      <button
                        onClick={() => attendMutation.mutate(event.id)}
                        disabled={attendMutation.isPending}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          event.is_attending
                            ? 'bg-green-100 text-green-700 hover:bg-red-50 hover:text-red-600'
                            : 'bg-[#0B1F3A] text-white hover:bg-[#1A3459]'
                        }`}
                      >
                        {event.is_attending ? 'Afmelden' : 'Aanmelden'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Kanaalregels */}
          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">{t('community.channelRulesTitle')}</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('community.rule1')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('community.rule2')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('community.rule3')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('community.rule4')}
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
    </CommunityLayout>
  );
};
